import React, { useEffect, useRef, useState } from "react";

import ScrollToBottomButton from "@/components/structure/ScrollToBottomButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface PeekoutDrawerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  title?: string | React.ReactNode;
  shortName?: string;
  onClose: () => void;
  children: React.ReactNode;
}

export default function PeekoutDrawer({
  open,
  setOpen,
  title,
  shortName,
  onClose,
  children
}: PeekoutDrawerProps) {
  const sheetRef = useRef(null);
  const contentRef = useRef<HTMLElement>(null);

  // ESC to close, and focus the sheet when opened
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
      };
    }

    window.addEventListener("keydown", onKeyDown);

    if (open) {
      sheetRef.current?.focus();
    }

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  return (
    <div>
      {/* Backdrop */}
      <div
        className={[
          "fixed inset-0 z-40 bg-black/40 transition-opacity",
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
        ].join(" ")}
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        role="dialog"
        aria-modal={open ? "true" : "false"}
        tabIndex={-1}
        ref={sheetRef}
        className={[
          "fixed left-0 right-0 bottom-0 z-50 mx-auto",
          "w-full max-w-6xl px-6",
          "rounded-t-2xl bg-white shadow-4xl outline-none",
          "transition-transform duration-300 ease-out will-change-transform",
          // Height changes: peek when closed, mostly-screen when open
          open ? "translate-y-0" : "translate-y-[calc(100%-5.6rem)]",
        ].join(" ")}
        style={{
          // When open, limit to most of screen; when closed, still renders but "peeks"
          height: open ? "80vh" : "80vh",
        }}
      >
        {/* Grab handle / header */}
        <div className="sticky top-0 rounded-t-2xl bg-white">
          <div className="p-3" onClick={() => setOpen((v) => !v)}>
            <div className="mx-auto mt-2 h-1.5 w-10 rounded-full bg-gray-300" />
          </div>

          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-lg font-semibold">
              {title || "Panel"}
            </h2>

            <button
              className="rounded-md px-2 py-1 text-sm text-gray-500 hover:bg-gray-100"
              onClick={() => setOpen((v) => !v)}
            >
              {open
                ? <span>
                    <FontAwesomeIcon icon="chevron-down" className="mr-1" />
                    {`Hide ${shortName || title || "Panel"}`}
                  </span>
                : <span>
                    <FontAwesomeIcon icon="chevron-up" className="mr-1" />
                    {`Show ${shortName || title || "Panel"}`}
                  </span>
              }
            </button>
          </div>
        </div>

        {/* Content (scrolls when open) */}
        <div className="h-[calc(80vh-3.5rem)] overflow-y-auto px-4 pb-6" ref={contentRef}>
          {children}
        </div>

        { /* An absolutely-positioned "scroll to bottom" button that appears when the user has scrolled up */ }
        <ScrollToBottomButton scrollingElement={contentRef} />
      </div>
    </div>
  );
}
